/**
 * @file App shell: template, locale, dynamic form, Mustache preview, JSON backup, PDF or POST submit.
 * CRM: `importId` triggers cross-origin `fetch` to Talent Hub import API; JSON uses `fields`, optional `templateId` / `locale` / `profileImage`; success + id enables minimal UI.
 * @module main
 */

import {
	CV_TEMPLATES,
	fetchTemplateSource,
	getCvTemplate,
	getFirstVisibleTemplateId,
} from "./cvTemplates.js";
import { hideAppLoader } from "./appLoader.js";
import { mountCvForm } from "./formController.js";
import { renderCvHtml } from "./previewRenderer.js";
import {
	buildExportPayload,
	buildSubmitCvPayload,
	downloadJsonFile,
	downloadPdfFromElement,
	parseImportPayload,
} from "./exportImport.js";
import {
	readBilingualStore,
	writeBilingualStore,
	loadLocaleData,
	schedulePersistCvFormData,
	persistLocaleBundleNow,
} from "./cvStorage.js";
import { mergePrefillInto, parseCvUrlParams } from "./urlParams.js";
import {
	applyValidationErrors,
	clearValidationErrors,
	getCvRequiredValidationIssues,
} from "./formValidation.js";
import { buildCvPdfFilename } from "./cvPdfFilename.js";

const LOCALE_STORAGE_KEY = "wetag-cv-locale";
const TEMPLATE_STORAGE_KEY = "wetag-cv-template";

/** @type {const} */
const LOCALES = ["en", "es"];

const PDF_BUTTON_LABELS = { es: "Descargar PDF", en: "Download PDF" };
const SUBMIT_BUTTON_LABEL = "Enviar";

/** Talent Hub import-by-id (CRM must allow CORS from the CV builder origin). */
const CRM_TALENTHUB_IMPORT_BASE =
	"https://crm.wetag.ar/api/talenthub/import";

/**
 * @param {string} id
 * @returns {boolean}
 */
function isValidCrmImportId(id) {
	if (!id || id.length > 128) return false;
	if (/[/\\]/.test(id) || id.includes("..")) return false;
	if (/[\u0000-\u001f\u007f]/.test(id)) return false;
	return true;
}

/** Auto-dismiss ms for submit toast; 0 = only close control. */
const SUBMIT_TOAST_AUTO_DISMISS_MS = 0;

let submitToastEl = null;
let submitToastTimer = 0;

function dismissSubmitToast() {
	if (submitToastTimer) {
		window.clearTimeout(submitToastTimer);
		submitToastTimer = 0;
	}
	submitToastEl?.remove();
	submitToastEl = null;
}

function showBusyToast(message) {
	dismissSubmitToast();
	const wrap = document.createElement("div");
	wrap.className = "app-toast app-toast--warning app-toast--sending";
	wrap.setAttribute("role", "status");
	wrap.setAttribute("aria-busy", "true");
	const text = document.createElement("p");
	text.className = "app-toast__text";
	text.textContent = message;
	wrap.append(text);
	document.body.append(wrap);
	submitToastEl = wrap;
}

function showSubmitSendingToast() {
	showBusyToast("Enviando CV…");
}

/**
 * Toast de resultado (texto plano). Siempre en español (el CV puede editarse en EN/ES).
 * @param {string} message
 * @param {"success" | "warning" | "danger"} [toastClass="danger"]
 */
function showSubmitResponseToast(message, toastClass = "danger") {
	dismissSubmitToast();
	const wrap = document.createElement("div");
	wrap.className = `app-toast app-toast--${toastClass}`;
	wrap.setAttribute(
		"role",
		toastClass === "danger" ? "alert" : "status"
	);
	const text = document.createElement("p");
	text.className = "app-toast__text";
	text.textContent = message;
	const btn = document.createElement("button");
	btn.type = "button";
	btn.className = "app-toast__close";
	btn.setAttribute("aria-label", "Cerrar");
	btn.textContent = "×";
	btn.addEventListener("click", dismissSubmitToast);
	wrap.append(text, btn);
	document.body.append(wrap);
	submitToastEl = wrap;
	if (SUBMIT_TOAST_AUTO_DISMISS_MS > 0) {
		submitToastTimer = window.setTimeout(
			dismissSubmitToast,
			SUBMIT_TOAST_AUTO_DISMISS_MS
		);
	}
}

/**
 * @param {unknown} body
 * @returns {null | { html: string, class: string }}
 */
function parseSubmitResponseBody(body) {
	if (!body || typeof body !== "object" || Array.isArray(body)) return null;
	const o = body;
	const html = o.html;
	const cls = o.class;
	if (typeof html !== "string" || typeof cls !== "string") return null;
	const c = cls.trim().toLowerCase();
	const tier =
		c === "success" || c === "warning" || c === "danger" ? c : "warning";
	return { html: html.trim() || "—", class: tier };
}

const uiStrings = {
	en: {
		uploadImage: "Upload Photo",
		removeProfileImage: "Remove",
		recropProfileImage: "Crop",
		addListRow: "Add Item",
		removeListRow: "Remove Item",
		crop: {
			title: "Crop Profile Photo",
			zoom: "Zoom",
			hint: "Drag to frame your face. The result is a square for the CV circle.",
			apply: "Apply",
			cancel: "Cancel",
		},
	},
	es: {
		uploadImage: "Subir Foto",
		removeProfileImage: "Borrar",
		recropProfileImage: "Recortar",
		addListRow: "Agregar Item",
		removeListRow: "Quitar Item",
		crop: {
			title: "Recortar Foto de Perfil",
			zoom: "Zoom",
			hint: "Arrastrá para encuadrar. El resultado es un cuadrado para el círculo del CV.",
			apply: "Aplicar",
			cancel: "Cancelar",
		},
	},
};

/**
 * Agrupa actualizaciones en un solo `requestAnimationFrame`.
 * @param {() => void} run
 * @returns {() => void} Schedule one preview refresh per animation frame (`onUpdate`).
 */
function schedulePreviewUpdate(run) {
	let frame = 0;
	return () => {
		if (frame) cancelAnimationFrame(frame);
		frame = requestAnimationFrame(() => {
			frame = 0;
			run();
		});
	};
}

/**
 * Heurística de idioma del navegador (sin `localStorage`).
 * @returns {"en" | "es"}
 */
function getBrowserDefaultLocale() {
	if (
		typeof navigator !== "undefined" &&
		navigator.language &&
		navigator.language.toLowerCase().startsWith("es")
	) {
		return "es";
	}
	return "en";
}

/**
 * Locale inicial: `localStorage` o heurística del navegador.
 * @returns {"en" | "es"}
 */
function getInitialLocale() {
	try {
		const saved = localStorage.getItem(LOCALE_STORAGE_KEY);
		if (saved === "en" || saved === "es") return saved;
	} catch { }
	return getBrowserDefaultLocale();
}

/**
 * @param {"en" | "es"} locale
 */
function persistLocale(locale) {
	try {
		localStorage.setItem(LOCALE_STORAGE_KEY, locale);
	} catch { }
}

/**
 * Id inicial: último guardado; si era una plantilla `hidden`, primera visible del catálogo.
 * @returns {string}
 */
function getInitialTemplateId() {
	try {
		const id = localStorage.getItem(TEMPLATE_STORAGE_KEY);
		if (id && CV_TEMPLATES.some((t) => t.id === id)) {
			const entry = CV_TEMPLATES.find((t) => t.id === id);
			if (entry && entry.structure.hidden === true) {
				return getFirstVisibleTemplateId();
			}
			return id;
		}
	} catch { }
	return getFirstVisibleTemplateId();
}

/**
 * Rellena el selector; omite plantillas con `hidden: true` salvo `selectedId`.
 * @param {HTMLSelectElement} select
 * @param {string} selectedId Plantilla activa (siempre aparece).
 */
function fillTemplateSelect(select, selectedId) {
	select.innerHTML = "";
	for (const t of CV_TEMPLATES) {
		if (t.structure.hidden === true && t.id !== selectedId) continue;
		const opt = document.createElement("option");
		opt.value = t.id;
		opt.textContent = String(
			t.structure && typeof t.structure.name === "string"
				? t.structure.name
				: t.id
		);
		select.append(opt);
	}
}

/**
 * `html[lang]` y estado de los botones de idioma.
 * @param {"en" | "es"} locale
 */
function applyLocaleDocumentAndToggle(locale) {
	document.documentElement.lang = locale;

	const btnEn = document.getElementById("btnLangEn");
	const btnEs = document.getElementById("btnLangEs");
	for (const btn of [btnEn, btnEs]) {
		if (!btn) continue;
		const isActive = btn.dataset.locale === locale;
		btn.classList.toggle("is-active", isActive);
		btn.setAttribute("aria-pressed", isActive ? "true" : "false");
	}
}

/**
 * Binds DOM, loads template text, applies URL/CRM/local data, attaches listeners.
 * @returns {Promise<void>}
 */
async function bootstrap() {
	try {
	const formFieldsMount = document.getElementById("cvFormFieldsMount");
	const previewRoot = document.getElementById("cvPreviewRoot");
	const btnJson = document.getElementById("btnDownloadJson");
	const btnPdf = document.getElementById("btnDownloadPdf");
	const inputImport = document.getElementById("inputImportJson");
	const btnLangEn = document.getElementById("btnLangEn");
	const btnLangEs = document.getElementById("btnLangEs");
	const templateSelect = document.getElementById("cvTemplateSelect");
	const formSettingsEl = document.querySelector(
		".cv-form-root > .cv-form-settings"
	);
	const headerIntroEl = document.querySelector(".app-header__intro");

	if (
		!formFieldsMount ||
		!previewRoot ||
		!btnJson ||
		!btnPdf ||
		!inputImport ||
		!templateSelect
	) {
		return;
	}

	const urlBoot = parseCvUrlParams(CV_TEMPLATES);

	let crmPayload = /** @type {Record<string, unknown> | null} */ (null);
	if (urlBoot.importId) {
		if (!isValidCrmImportId(urlBoot.importId)) {
			showSubmitResponseToast(
				"El id de importación no es válido.",
				"danger"
			);
		} else {
			try {
				const res = await fetch(
					`${CRM_TALENTHUB_IMPORT_BASE}/${encodeURIComponent(urlBoot.importId)}`,
					{ headers: { Accept: "application/json" } }
				);
				let body;
				try {
					body = await res.json();
				} catch {
					showSubmitResponseToast(
						"Respuesta inválida del import CRM.",
						"danger"
					);
					body = null;
				}

				if (body != null) {
					if (!res.ok) {
						const msg =
							typeof body.error === "string"
								? body.error
								: `HTTP ${res.status}`;
						showSubmitResponseToast(msg, "danger");
					} else {
						crmPayload =
							body && typeof body === "object" && !Array.isArray(body)
								? /** @type {Record<string, unknown>} */ (body)
								: null;
					}
				}
			} catch (e) {
				console.error("[cv] crm import:", e);
				showSubmitResponseToast(
					"No se pudieron cargar los datos desde el CRM.",
					"danger"
				);
			}
		}
	}

	const crmImportMinimalUi = Boolean(urlBoot.importId && crmPayload);
	const skipLocalBootstrap = Boolean(urlBoot.importId);

	const crmTemplateIdRaw = crmPayload?.templateId ?? crmPayload?.templateid;
	const crmTemplateId =
		typeof crmTemplateIdRaw === "string" &&
			CV_TEMPLATES.some((t) => t.id === crmTemplateIdRaw.trim())
			? crmTemplateIdRaw.trim()
			: null;
	const crmLocaleRaw = crmPayload?.locale;
	const crmLocale =
		crmLocaleRaw === "en" || crmLocaleRaw === "es"
			? crmLocaleRaw
			: null;

	const lockTemplate = urlBoot.lockTemplate || Boolean(crmTemplateId);
	const lockLocale = urlBoot.lockLocale || Boolean(crmLocale);
	const submitUrl = urlBoot.submitUrl;
	const submitMode = urlBoot.hasSubmitUrl;
	const additionalDataForSubmit = urlBoot.additionalData;

	let activeTemplateId =
		crmTemplateId ??
		urlBoot.templateId ??
		(skipLocalBootstrap ? getFirstVisibleTemplateId() : getInitialTemplateId());
	fillTemplateSelect(templateSelect, activeTemplateId);
	const initialTemplate = getCvTemplate(activeTemplateId);
	/** @type {typeof initialTemplate.structure} */
	let structure = initialTemplate.structure;
	let templateSource = await fetchTemplateSource(initialTemplate.templateUrl);
	templateSelect.value = activeTemplateId;

	let formApi = null;

	let submitRequestInFlight = false;

	let locale =
		crmLocale ??
		urlBoot.locale ??
		(skipLocalBootstrap ? getBrowserDefaultLocale() : getInitialLocale());
	if (urlBoot.locale || crmLocale) {
		persistLocale(locale);
	}
	applyLocaleDocumentAndToggle(locale);

	if (urlBoot.templateId || crmTemplateId) {
		try {
			localStorage.setItem(TEMPLATE_STORAGE_KEY, activeTemplateId);
		} catch { }
	}

	function refreshPrimaryButtonLabel() {
		if (!btnPdf) return;
		if (submitMode) {
			btnPdf.textContent = SUBMIT_BUTTON_LABEL;
		} else {
			btnPdf.textContent = PDF_BUTTON_LABELS.es;
		}
	}

	refreshPrimaryButtonLabel();

	function validateRequiredForExport() {
		if (!formApi) return true;
		clearValidationErrors(formFieldsMount);
		const issues = getCvRequiredValidationIssues(
			formApi.readData(),
			structure
		);
		if (issues.length === 0) return true;
		applyValidationErrors(formFieldsMount, issues);
		formFieldsMount
			.querySelector(".form-field.has-error, fieldset.has-error")
			?.scrollIntoView({ behavior: "smooth", block: "nearest" });
		return false;
	}

	if (lockTemplate) {
		templateSelect.disabled = true;
		templateSelect.setAttribute("aria-disabled", "true");
		templateSelect.classList.add("cv-form-template__select--locked");
	}
	if (lockLocale) {
		for (const b of [btnLangEn, btnLangEs]) {
			if (!b) continue;
			b.disabled = true;
			b.setAttribute("aria-disabled", "true");
			b.classList.add("cv-form-locale__btn--locked");
		}
	}

	const pushPreview = () => {
		if (!formApi) return;
		const data = formApi.readData();
		previewRoot.innerHTML = renderCvHtml(
			templateSource,
			data,
			structure,
			locale
		);
	};

	const onUpdate = schedulePreviewUpdate(() => {
		pushPreview();
		if (formApi) {
			schedulePersistCvFormData(() => formApi.readData(), () => locale);
		}
	});

	/**
	 * Applies minimal header/form chrome for CRM open (`importId` + successful payload).
	 */
	function applyCrmImportMinimalShell() {
		const loadBackupLabel = inputImport?.closest("label") ?? null;
		if (crmImportMinimalUi) {
			if (headerIntroEl) headerIntroEl.hidden = true;
			if (formSettingsEl) formSettingsEl.hidden = true;
			if (loadBackupLabel) loadBackupLabel.hidden = true;
			btnJson.hidden = true;
			for (const el of formFieldsMount.querySelectorAll(
				"fieldset[data-section-id]"
			)) {
				el.hidden = el.dataset.sectionId !== "profilePhoto";
			}
		} else {
			if (headerIntroEl) headerIntroEl.hidden = false;
			if (formSettingsEl) formSettingsEl.hidden = false;
			if (loadBackupLabel) loadBackupLabel.hidden = false;
			btnJson.hidden = false;
			for (const el of formFieldsMount.querySelectorAll(
				"fieldset[data-section-id]"
			)) {
				el.hidden = false;
			}
		}
	}

	/**
	 * @param {Record<string, unknown> | undefined} initialOverride Datos tras el mount; si se omite, conserva el estado actual.
	 */
	function remountForm(initialOverride) {
		const previous =
			initialOverride !== undefined
				? initialOverride
				: formApi
					? formApi.readData()
					: {};
		formApi = mountCvForm({
			container: formFieldsMount,
			structure,
			onUpdate,
			locale,
			messages: {
				uploadImage: uiStrings[locale].uploadImage,
				removeProfileImage: uiStrings[locale].removeProfileImage,
				recropProfileImage: uiStrings[locale].recropProfileImage,
				addListRow: uiStrings[locale].addListRow,
				removeListRow: uiStrings[locale].removeListRow,
				crop: uiStrings[locale].crop,
			},
		});
		formApi.applyData(previous);
		applyCrmImportMinimalShell();
	}

	/**
	 * @param {string} nextId Id válido (ver `CV_TEMPLATES` en `cvTemplates.js`).
	 * @param {{ skipPersist?: boolean, force?: boolean }} [opts] `skipPersist`: no persistir antes de cambiar (import). `force`: ignorar `lockTemplate` (import).
	 * @returns {Promise<void>}
	 */
	async function switchTemplate(nextId, opts = {}) {
		if (lockTemplate && !opts.force) return;
		if (nextId === activeTemplateId) return;
		const entry = CV_TEMPLATES.find((t) => t.id === nextId);
		if (!entry) return;

		if (!opts.skipPersist && formApi) {
			persistLocaleBundleNow(() => formApi.readData(), locale);
		}

		activeTemplateId = entry.id;
		try {
			localStorage.setItem(TEMPLATE_STORAGE_KEY, activeTemplateId);
		} catch { }

		structure = entry.structure;
		templateSource = await fetchTemplateSource(entry.templateUrl);
		fillTemplateSelect(templateSelect, activeTemplateId);
		templateSelect.value = activeTemplateId;
		remountForm(loadLocaleData(locale));
		pushPreview();
	}

	templateSelect.addEventListener("change", () => {
		void switchTemplate(templateSelect.value);
	});

	function buildInitialFormData() {
		if (skipLocalBootstrap) {
			let base = {};
			if (crmPayload) {
				const fields = crmPayload.fields;
				if (fields && typeof fields === "object" && !Array.isArray(fields)) {
					base = mergePrefillInto(
						base,
            /** @type {Record<string, unknown>} */ (fields)
					);
				}
				const img = crmPayload.profileImage;
				if (typeof img === "string" && img.startsWith("data:")) {
					base = mergePrefillInto(base, { profileImage: img });
				}
			}
			return base;
		}

		let base = loadLocaleData(locale);
		if (urlBoot.prefill && typeof urlBoot.prefill === "object") {
			base = mergePrefillInto(base, urlBoot.prefill);
		}
		return base;
	}

	remountForm(buildInitialFormData());
	pushPreview();

	window.addEventListener("beforeunload", () => {
		if (formApi) {
			persistLocaleBundleNow(() => formApi.readData(), locale);
		}
	});

	/**
	 * @param {"en" | "es"} next
	 */
	function setLocale(next) {
		if (lockLocale) return;
		if (!LOCALES.includes(next) || next === locale) return;
		if (formApi) {
			persistLocaleBundleNow(() => formApi.readData(), locale);
		}
		locale = next;
		persistLocale(locale);
		applyLocaleDocumentAndToggle(locale);
		refreshPrimaryButtonLabel();
		remountForm(loadLocaleData(locale));
	}

	btnLangEn?.addEventListener("click", () => setLocale("en"));
	btnLangEs?.addEventListener("click", () => setLocale("es"));

	btnJson.addEventListener("click", () => {
		if (formApi) {
			persistLocaleBundleNow(() => formApi.readData(), locale);
		}
		const payload = buildExportPayload(readBilingualStore(), structure.id);
		downloadJsonFile(payload, "cv-backup.json");
	});

	btnPdf.addEventListener("click", async () => {
		if (formApi) {
			persistLocaleBundleNow(() => formApi.readData(), locale);
		}
		if (submitMode) {
			if (!submitUrl || !formApi) return;
			if (!validateRequiredForExport()) return;
			if (submitRequestInFlight) return;
			submitRequestInFlight = true;
			btnPdf.disabled = true;
			btnPdf.setAttribute("aria-busy", "true");
			btnPdf.classList.add("is-submitting");
			try {
				showSubmitSendingToast();
				const payload = {
					additionalData: additionalDataForSubmit,
					cv: buildSubmitCvPayload(
						() => formApi.readData(),
						structure.id,
						locale
					),
				};
				const res = await fetch(submitUrl, {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify(payload),
					mode: "cors",
					credentials: "omit",
				});
				const rawText = await res.text();
				if (!res.ok) {
					throw new Error(`HTTP ${res.status}`);
				}
				let parsedBody = null;
				try {
					parsedBody = rawText ? JSON.parse(rawText) : null;
				} catch {
					parsedBody = null;
				}
				const submitMsg = parseSubmitResponseBody(parsedBody);
				if (submitMsg) {
					showSubmitResponseToast(submitMsg.html, submitMsg.class);
				} else {
					showSubmitResponseToast("Enviado correctamente.", "success");
				}
			} catch (e) {
				console.error(e);
				dismissSubmitToast();
				window.alert("Error al enviar.");
			} finally {
				submitRequestInFlight = false;
				btnPdf.disabled = false;
				btnPdf.removeAttribute("aria-busy");
				btnPdf.classList.remove("is-submitting");
				refreshPrimaryButtonLabel();
			}
			return;
		}
		if (!formApi || !validateRequiredForExport()) return;
		const data = formApi.readData();
		const pdfName = buildCvPdfFilename(data, locale);
		btnPdf.disabled = true;
		btnPdf.setAttribute("aria-busy", "true");
		showBusyToast("Preparando tu CV…");
		try {
			await downloadPdfFromElement(previewRoot, pdfName);
			dismissSubmitToast();
		} catch (e) {
			console.error(e);
			dismissSubmitToast();
		} finally {
			btnPdf.disabled = false;
			btnPdf.removeAttribute("aria-busy");
		}
	});

	inputImport.addEventListener("change", () => {
		const file = inputImport.files && inputImport.files[0];
		inputImport.value = "";
		if (!file) return;
		const reader = new FileReader();
		reader.onload = () => {
			void (async () => {
				try {
					const parsed = JSON.parse(String(reader.result));
					const imported = parseImportPayload(parsed);
					if (!imported) {
						console.warn(
							"Invalid or corrupted backup file / Archivo de backup inválido"
						);
						return;
					}
					writeBilingualStore({
						version: 1,
						...(imported.profileImage
							? { profileImage: imported.profileImage }
							: {}),
						locales: imported.locales,
					});
					const backupTemplateId =
						typeof imported.templateId === "string"
							? imported.templateId
							: "";
					const canSwitch =
						backupTemplateId &&
						CV_TEMPLATES.some((t) => t.id === backupTemplateId);
					if (canSwitch && backupTemplateId !== activeTemplateId) {
						await switchTemplate(backupTemplateId, {
							skipPersist: true,
							force: true,
						});
					} else {
						if (
							backupTemplateId &&
							backupTemplateId !== structure.id &&
							!canSwitch
						) {
							console.warn(
								`Backup template "${backupTemplateId}" is not available; using "${structure.id}".`
							);
						} else if (backupTemplateId && backupTemplateId !== structure.id) {
							console.warn(
								"Template id mismatch; applying data with current template anyway."
							);
						}
						formApi.applyData(loadLocaleData(locale));
						pushPreview();
					}
				} catch (err) {
					console.error(err);
				}
			})();
		};
		reader.readAsText(file);
	});
	} finally {
		hideAppLoader();
	}
}

bootstrap();
